import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useAuth } from '../store/authStore.js'
import { api } from '../lib/api.js'
import { CheckCircle2, AlertCircle, KeyRound } from 'lucide-react'
import opLogo from '../assets/op.png'
import Footer from '../components/layout/Footer.jsx'

export default function AcceptInvite() {
  const { token } = useParams()
  const nav = useNavigate()
  const acceptInvite = useAuth(s => s.acceptInvite)

  const [state, setState] = useState({ loading: true, info: null, error: null })
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  useEffect(() => {
    api.checkInvite(token)
      .then(info => setState({ loading: false, info, error: null }))
      .catch(e => setState({ loading: false, info: null, error: e.message }))
  }, [token])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError(null)
    if (password.length < 8) return setSubmitError('Passwort muss mindestens 8 Zeichen haben.')
    if (password !== confirm) return setSubmitError('Passwörter stimmen nicht überein.')
    setSubmitting(true)
    try {
      await acceptInvite(token, password)
      nav('/')
    } catch (err) {
      setSubmitError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-teal-50 via-white to-teal-100/50">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={opLogo} alt="O+P Consult" className="h-14 w-auto mx-auto" />
          <p className="font-serif text-[18px] font-medium tracking-[-0.015em] text-ink-soft mt-3">mindnet&thinsp;·&thinsp;academy</p>
        </div>

        <div className="card p-8">
          {state.loading && <p className="text-ink-soft text-center">Einladung wird geprüft…</p>}

          {!state.loading && state.error && (
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 grid place-items-center mx-auto mb-4">
                <AlertCircle size={24} />
              </div>
              <h1 className="text-xl font-bold mb-2">Einladung ungültig</h1>
              <p className="text-ink-soft mb-6 text-sm">{state.error}</p>
              <p className="text-ink-mute text-xs mb-4">Bitte bitten Sie Ihre Administration um einen neuen Einladungslink.</p>
              <Link to="/login" className="btn-secondary">Zur Anmeldung</Link>
            </div>
          )}

          {!state.loading && state.info && (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-700 grid place-items-center shrink-0">
                  <KeyRound size={20} />
                </div>
                <div>
                  <h1 className="text-xl font-bold">
                    {state.info.purpose === 'reset' ? 'Passwort zurücksetzen' : 'Konto aktivieren'}
                  </h1>
                  <p className="text-sm text-ink-soft">
                    Hallo {state.info.user.firstName} {state.info.user.lastName}
                  </p>
                </div>
              </div>

              <p className="text-sm text-ink-soft mb-5">
                {state.info.purpose === 'reset'
                  ? 'Vergeben Sie ein neues Passwort für Ihr Konto. Bestehende Sitzungen werden anschließend abgemeldet.'
                  : 'Vergeben Sie ein Passwort, um Ihr Konto zu aktivieren.'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label">E-Mail-Adresse</label>
                  <input className="input bg-teal-50/40" disabled value={state.info.user.email} />
                </div>
                <div>
                  <label className="label">Neues Passwort</label>
                  <input
                    type="password" autoFocus required minLength={8} autoComplete="new-password"
                    className="input"
                    value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Mindestens 8 Zeichen"
                  />
                </div>
                <div>
                  <label className="label">Passwort wiederholen</label>
                  <input
                    type="password" required minLength={8} autoComplete="new-password"
                    className="input"
                    value={confirm} onChange={e => setConfirm(e.target.value)}
                  />
                </div>
                {submitError && (
                  <div className="flex items-start gap-2 text-rose-600 text-sm">
                    <AlertCircle size={16} className="mt-0.5" />
                    <span>{submitError}</span>
                  </div>
                )}
                <button type="submit" disabled={submitting} className="btn-primary w-full">
                  <CheckCircle2 size={18} />
                  {submitting ? 'Speichert…' : (state.info.purpose === 'reset' ? 'Passwort speichern' : 'Konto aktivieren')}
                </button>
              </form>
            </>
          )}
        </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}
